import type {
  AdminApprovalRequest,
  AdminCaseUpdate,
  AdminPendingCaseDto,
  AdminRejectionRequest,
  AuthConfigDto,
  CaseGenerationBatchDto,
  CaseDto,
  CaseSelection,
  CurrentUserDto,
  GenerateCasesRequest,
  GenerateCasesResponse,
  GameUserDto,
  SessionCompletedEvent,
  SessionDto,
  TaxonomySectorDto,
} from "./types";
import { readSseStream, type SseEvent } from "./sse";

/** Provides authenticated access to the game and admin review APIs. */
export class GameApiClient {
  public constructor(
    private readonly getAccessToken: () => string | null | Promise<string | null>,
    private readonly baseUrl: string = "",
  ) {}

  public async getAuthConfig(): Promise<AuthConfigDto> {
    const response = await fetch(`${this.baseUrl}/api/auth/config`);
    if (!response.ok)
      throw new Error("Kunne ikke hente innstillinger for innlogging.");
    return await response.json() as AuthConfigDto;
  }

  public async getTaxonomy(): Promise<readonly TaxonomySectorDto[]> {
    const response = await this.request("/api/cases/taxonomy");
    if (!response.ok)
      throw new Error("Kunne ikke hente emneoversikten.");
    return await response.json() as TaxonomySectorDto[];
  }

  public async getRandomCase(selection: CaseSelection): Promise<CaseDto> {
    const query = new URLSearchParams({
      difficulty: selection.difficulty,
      sector: selection.sector,
      topic: selection.topic,
    });
    const response = await this.request(`/api/cases/random?${query.toString()}`);
    if (response.status === 404)
      throw new Error("Ingen aktive caser funnet for dette valget.");
    if (!response.ok)
      throw new Error("Kunne ikke hente case.");
    return await response.json() as CaseDto;
  }

  public async startSession(caseId: string): Promise<SessionDto> {
    const response = await this.request("/api/sessions", {
      method: "POST",
      body: JSON.stringify({ caseId }),
    });
    if (!response.ok)
      throw new Error("Kunne ikke starte økten.");
    return await response.json() as SessionDto;
  }

  public async sendArgument(
    sessionId: string,
    content: string,
    onEvent: (event: SseEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await this.request(`/api/sessions/${sessionId}/messages`, {
      method: "POST",
      body: JSON.stringify({ content }),
      signal,
    });
    if (!response.ok)
      throw new Error("Kunne ikke sende argumentet.");
    await readSseStream(response, onEvent, signal);
  }

  public async requestHint(
    sessionId: string,
    onEvent: (event: SseEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await this.request(`/api/sessions/${sessionId}/hints`, {
      method: "POST",
      signal,
    });
    if (!response.ok)
      throw new Error("Kunne ikke hente hint.");
    await readSseStream(response, onEvent, signal);
  }

  public async giveUp(sessionId: string): Promise<SessionCompletedEvent> {
    const response = await this.request(`/api/sessions/${sessionId}/give-up`, {
      method: "POST",
    });
    if (!response.ok)
      throw new Error("Kunne ikke avslutte caset.");
    return await response.json() as SessionCompletedEvent;
  }

  public async getPendingAdminCases(): Promise<readonly AdminPendingCaseDto[]> {
    const response = await this.request("/api/admin/cases/pending");
    if (!response.ok)
      throw new Error("Kunne ikke hente caser til faglig kontroll.");
    return await response.json() as AdminPendingCaseDto[];
  }

  public async updatePendingCase(caseId: string, update: AdminCaseUpdate): Promise<AdminPendingCaseDto> {
    const response = await this.request(`/api/admin/cases/${encodeURIComponent(caseId)}`, {
      method: "PUT",
      body: JSON.stringify(update),
    });
    if (!response.ok)
      throw new Error("Kunne ikke lagre endringene.");
    return await response.json() as AdminPendingCaseDto;
  }

  public async rejectPendingCase(caseId: string, reason: string): Promise<void> {
    const payload: AdminRejectionRequest = { reason };
    const response = await this.request(`/api/admin/cases/${encodeURIComponent(caseId)}/reject`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    if (!response.ok)
      throw new Error("Kunne ikke avvise caset.");
  }

  public async approvePendingCase(caseId: string, approval: AdminApprovalRequest): Promise<void> {
    const response = await this.request(`/api/admin/cases/${encodeURIComponent(caseId)}/approve`, {
      method: "POST",
      body: JSON.stringify(approval),
    });
    if (!response.ok)
      throw new Error("Kunne ikke godkjenne caset.");
  }

  public async getCurrentUser(): Promise<CurrentUserDto> {
    const response = await this.request("/api/auth/me");
    if (!response.ok)
      throw new Error("Kunne ikke hente brukerprofilen.");
    return await response.json() as CurrentUserDto;
  }

  public async generateCases(request: GenerateCasesRequest): Promise<CaseGenerationBatchDto> {
    const response = await this.request("/api/admin/cases/generate", {
      method: "POST",
      body: JSON.stringify(request),
    });
    if (!response.ok)
      throw new Error("Kunne ikke generere caser.");
    const result = await response.json() as GenerateCasesResponse;
    return result.batch;
  }

  public async getCaseGenerationBatches(): Promise<readonly CaseGenerationBatchDto[]> {
    const response = await this.request("/api/admin/cases/generation-batches");
    if (!response.ok)
      throw new Error("Kunne ikke hente status for generering.");
    return await response.json() as readonly CaseGenerationBatchDto[];
  }

  public async getAdminUsers(): Promise<readonly GameUserDto[]> {
    const response = await this.request("/api/admin/users");
    if (!response.ok)
      throw new Error("Kunne ikke hente brukere.");
    return await response.json() as readonly GameUserDto[];
  }

  public async setUserRole(userId: string, role: GameUserDto["role"]): Promise<GameUserDto> {
    const response = await this.request(`/api/admin/users/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    });
    if (!response.ok)
      throw new Error("Kunne ikke oppdatere rollen.");
    return await response.json() as GameUserDto;
  }

  private async request(path: string, init: RequestInit = {}): Promise<Response> {
    const token = await this.getAccessToken();
    if (token === null)
      throw new Error("Du må være innlogget for å spille.");

    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);
    if (init.body !== undefined)
      headers.set("Content-Type", "application/json");

    return await fetch(`${this.baseUrl}${path}`, { ...init, headers });
  }
}
